// File excluded from token migration due to custom illustration
// https://atlassian.slack.com/archives/CFJ9DU39U/p1709665443238399
import React from 'react';
import { styled } from '@compiled/react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import Undo from '@atlaskit/icon/glyph/undo';
import Redo from '@atlaskit/icon/glyph/redo';
import TextStyle from '@atlaskit/icon/glyph/editor/text-style';
import AlignLeftIcon from '@atlaskit/icon/glyph/editor/align-left';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import CheckboxIcon from '@atlaskit/icon/glyph/checkbox';
import LinkIcon from '@atlaskit/icon/glyph/editor/link';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';

import TextColor from '../assets/TextColor.svg';
import BlueConfluenceIcon from '../assets/BlueConfluenceIcon.svg';
import MeepleVania from '../assets/MeepleVania.svg';
import MeepleKatina from '../assets/MeepleKatina.svg';
import MeepleLeo from '../assets/MeepleLeo.svg';
import MeepleMilo from '../assets/MeepleMilo.svg';
import InviteIllustration from '../assets/InviteIllustration.svg';

import {
	EditBar,
	ConfluenceIconGroup,
	ConfluenceIcon,
	IconGroup,
	IconSizeAdjuster,
	TextColorIcon,
	AvatarContainer,
	Avatar,
	EditorItemSkeleton,
	IconChevronGroup,
} from './EditBar';
import {
	PageContentArea,
	PageContentTextSkeleton,
	PageNoteMacroSkeleton,
	CheckboxContainer,
	PageContentTitle,
	PageContentPurpleSubtitle,
	PageContentSubtitle,
	PageColumnsWrapper,
	PageColumn,
} from './PageContent';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IllustrationContainer = styled.div({
	width: '286px',
	height: '149px',
	marginTop: token('space.300', '24px'),
	backgroundColor: token('elevation.surface', '#FFFFFF'),
	borderRadius: '4px 4px 0 0',
	borderBottom: `1px solid ${token('color.background.accent.gray.subtler', '#DCDFE4')}`,
	'@media screen and (max-width: 375px)': {
		marginTop: token('space.600', '48px'),
	},
	'@media screen and (max-width: 1024px)': {
		marginTop: token('space.300', '24px'),
	},
});

const AVATARS = [null, MeepleMilo, MeepleKatina, MeepleLeo, MeepleVania];

const templateBannerIllustrationi18n = defineMessages({
	templateTitle: {
		id: 'onboarding-editor.template.banner.illustration.template.title',
		defaultMessage: 'Create and edit content',
		description: 'Title of a template called Create and Edit Content',
	},
	publishSubtitle: {
		id: 'onboarding-editor.template.banner.illustration.publish.subtitle',
		defaultMessage: 'Publishing pages',
		description: 'Subtitle for publishing pages',
	},
	editSubtitle: {
		id: 'onboarding-editor.template.banner.illustration.edit.subtitle.',
		defaultMessage: 'Edit and publish',
		description: 'Subtitle for edit and publish pages',
	},
});

// Note: this is used for Land In Editor Banner
export const TemplateBannerIllustration = () => {
	return (
		<IllustrationContainer>
			<EditBar>
				<ConfluenceIconGroup>
					<ConfluenceIcon src={BlueConfluenceIcon} alt="Confluence Icon" />
					<IconSizeAdjuster>
						<Undo size="small" label="undo" />
					</IconSizeAdjuster>{' '}
					<IconSizeAdjuster>
						<Redo size="small" label="redo" />
					</IconSizeAdjuster>
				</ConfluenceIconGroup>
				<IconGroup>
					<IconChevronGroup>
						<IconSizeAdjuster>
							<TextStyle size="small" label="text style" />
						</IconSizeAdjuster>
						<IconSizeAdjuster>
							<ChevronDownIcon size="small" label="chevron down" />
						</IconSizeAdjuster>
					</IconChevronGroup>
				</IconGroup>
				<IconGroup>
					<IconChevronGroup>
						<IconSizeAdjuster>
							<AlignLeftIcon size="small" label="align left" />
						</IconSizeAdjuster>
						<IconSizeAdjuster>
							<ChevronDownIcon size="small" label="chevron down" />
						</IconSizeAdjuster>
					</IconChevronGroup>
				</IconGroup>
				<IconGroup>
					<IconChevronGroup>
						<IconSizeAdjuster>
							<TextColorIcon src={TextColor} alt="text color" />
						</IconSizeAdjuster>
						<IconSizeAdjuster>
							<ChevronDownIcon size="small" label="chevron down" />
						</IconSizeAdjuster>
					</IconChevronGroup>
					<IconSizeAdjuster>
						<CheckboxIcon size="small" label="checkbox" />
					</IconSizeAdjuster>
					<IconSizeAdjuster>
						<LinkIcon size="small" label="link" />
					</IconSizeAdjuster>
					<IconChevronGroup>
						<IconSizeAdjuster>
							<EditorAddIcon size="small" label="plus" />
						</IconSizeAdjuster>
						<IconSizeAdjuster>
							<ChevronDownIcon size="small" label="chevron down" />
						</IconSizeAdjuster>
					</IconChevronGroup>
				</IconGroup>
				<AvatarContainer>
					{AVATARS.map((avatar, index) => {
						return (
							<Avatar
								src={avatar}
								zIndex={index + 1}
								right={`${index * 10}px`}
								key={`avatar-${index}`}
							>
								{!avatar && '+3'}
							</Avatar>
						);
					})}
				</AvatarContainer>
				<EditorItemSkeleton
					width={19}
					color={token('color.background.accent.blue.bolder', '#0C66E4')}
				/>
				<EditorItemSkeleton width={9} />
				<EditorItemSkeleton width={23} />
				<EditorItemSkeleton width={10} marginRight={6} />
			</EditBar>
			<PageContentArea>
				<PageContentTitle>
					<FormattedMessage {...templateBannerIllustrationi18n.templateTitle} />
				</PageContentTitle>
				<PageNoteMacroSkeleton>
					<PageContentPurpleSubtitle>
						<FormattedMessage {...templateBannerIllustrationi18n.publishSubtitle} />
					</PageContentPurpleSubtitle>
					<PageContentTextSkeleton width="223" />
					<PageContentTextSkeleton width="143" />
				</PageNoteMacroSkeleton>

				<CheckboxContainer>
					<CheckboxIcon size="small" label="checkbox" />
					<PageContentTextSkeleton
						width="76"
						backgroundColor={token('color.background.accent.purple.subtlest', '#F3F0FF')}
						marginLeft="6.5"
						marginBottom="0"
					/>
				</CheckboxContainer>

				<PageColumnsWrapper>
					<PageColumn>
						<PageContentSubtitle>
							<FormattedMessage {...templateBannerIllustrationi18n.editSubtitle} />
						</PageContentSubtitle>
						<PageContentTextSkeleton
							width="118"
							backgroundColor={token('color.background.accent.gray.subtler', '#DCDFE4')}
							marginBottom="4.5"
						/>
						<PageContentTextSkeleton
							width="102"
							backgroundColor={token('color.background.accent.gray.subtler', '#DCDFE4')}
							marginBottom="4.5"
						/>
						<PageContentTextSkeleton
							width="66"
							backgroundColor={token('color.background.accent.gray.subtler', '#DCDFE4')}
						/>
					</PageColumn>
					<img src={InviteIllustration} alt="" width={102} />
				</PageColumnsWrapper>
			</PageContentArea>
		</IllustrationContainer>
	);
};
